import { DOCUMENT, NgClass, NgStyle } from '@angular/common';
import {
  AfterContentChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  Output,
  OnDestroy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { StaticFileHash } from '@scalefast/ecommerce-core';

import { ExtractBazaarvoiceID } from 'src/app/shared/pipes/extract-bazaarvoice-id.pipe';
import { JebbitProduct } from '../../../../../core/interfaces/jebbit.interface';
import { TooltipDirective } from '../../../../../shared/directives/tooltip.directive';
import { AppBazaarvoiceComponent } from '../../../bazaarvoice/bazaarvoice.component';

@Component({
  standalone: true,
  imports: [
    NgClass,
    NgStyle,
    FormsModule,
    TooltipDirective,
    AppBazaarvoiceComponent,
    RouterModule,
    StaticFileHash,
    ExtractBazaarvoiceID,
  ],
  selector: 'app-recommended-comparison-element',
  templateUrl: './comparison-element.component.html',
  styleUrls: ['./comparison-element.styles.scss'],
})
export class AppRecommendedComparisonComponent implements AfterViewInit, AfterContentChecked, OnDestroy {
  @Input() index: number = 0;
  @Input() bazaarvoiceReviews: boolean = true;
  @Input() isHubPage: boolean = true;
  @Input() products: JebbitProduct[] = [];
  @Input() displayAccesories: boolean = false;
  @Output() public displayAccesoriesEvent = new EventEmitter<boolean>();
  @Input() displayModes: boolean = false;
  @Output() public displayModesEvent = new EventEmitter<boolean>();
  @Input()
  set initialSelected(d: any) {
    const product = this.products.find((product) => product.fields.jebbitId == d.fields.jebbitId);
    if (product) {
      this.selectedProduct = product;
      this.selectedJebbitID = product.fields.jebbitId;
    }
  }

  adjustHeight: number = 48;
  selectedJebbitID: string = '';
  selectedProduct: JebbitProduct = new JebbitProduct();
  isMobile: boolean = false;

  titleHeights: any[] = [];
  titleMaxHeights: number[] = [];
  resizeObservers: ResizeObserver[] = [];

  constructor(
    @Inject(DOCUMENT) private _doc: Document,
    private elementRef: ElementRef,
  ) {}

  ngAfterViewInit(): void {
    this.resizeChange();
  }

  ngAfterContentChecked(): void {
    for (let i = 0; i < this.selectedProduct.fields.prePurchaseComparePoints.length + 1; i++) {
      let index = i;
      const titles = this._doc.querySelectorAll('.comparison__entry-' + i + ' .comparison__inner_box');

      const titleObserver = new ResizeObserver((entries) => {
        entries.forEach((entry: any, i: number) => {
          const newHeight = entry.contentRect.height;
          const prevHeight = this.titleHeights[index] ? this.titleHeights[index][i] : undefined;
          if (newHeight !== prevHeight) this.setRowHeight(newHeight, index, i);
        });
      });

      if (titles) {
        titles.forEach((title) => {
          titleObserver.observe(title);
          this.resizeObservers.push(titleObserver);
        });
      }
    }
  }

  @HostListener('window:resize', [])
  onResize() {
    this.resizeChange();
  }

  resizeChange() {
    let docWidth = this._doc.getElementsByTagName('body')[0].clientWidth;
    if (docWidth < 768) {
      this.adjustHeight = 28;
      this.isMobile = true;
    } else {
      this.isMobile = false;
      this.adjustHeight = 48;
    }
  }

  setRowHeight(h: number, index: number, i: number) {
    if (h > 0)
      if (!this.titleHeights[index]) {
        this.titleHeights[index] = []; // Initialize if undefined
        this.titleHeights[index].push(h);
      } else {
        if (!this.titleHeights[index][i]) {
          this.titleHeights[index].push(h);
        }
        // Update titleHeight
        if (this.titleHeights[index][i] !== undefined) {
          this.titleHeights[index][i] = h;
        }
      }
    let entryHeight = Math.max(...this.titleHeights[index]);
    if (entryHeight > 0) {
      if (index === 7) {
        entryHeight = entryHeight + this.adjustHeight;
      } else {
        entryHeight = entryHeight + 66 + this.adjustHeight;
      }
      this.titleMaxHeights[index] = entryHeight;
      this._doc.querySelectorAll('.comparison__entry-' + index).forEach((entry: any) => {
        entry.style.height = this.titleMaxHeights[index] + 'px';
      });
    }
  }

  selectProduct(event: any) {
    const product = this.products.find((product) => product.fields.jebbitId == event.target.value);
    if (product) {
      this.selectedProduct = product;
    }
  }

  parseImageUrl(url: string): string {
    const matches = /\((.*?)\)/.exec(url);

    if (matches && matches[1]) {
      return matches[1];
    }

    return '';
  }

  selectedTracker(index: number, item: any) {
    return item.fields.jebbitId;
  }

  getAccesoriesHTML(accesories: string) {
    return accesories.replace(
      /^- (.+)$/gm,
      '<div class="recommendedbrushes_li-container"><span class="recommendedbrushes_point"> • </span><span><strong>$1</strong></span></div>',
    );
  }
  showAccesories(display: boolean) {
    this.displayAccesories = display;
    this.displayAccesoriesEvent.emit(display);
  }

  getModeImage(str: string) {
    const regex = /!\[.*?\]\((.*?)\)/;
    const match = str.match(regex);

    if (match && match.length > 1) {
      const imageUrl = match[1];
      return imageUrl;
    } else {
      return '';
    }
  }

  getModeContent(str: string) {
    const regex = /\n__(.*?)__\n/;
    const match = str.match(regex);

    if (match && match.length > 1) {
      const title = match[1].trim().replaceAll('_', '');
      const description = str.split(match[0])[1];
      return '<strong>' + title + '</strong><p clas="comparison__modes-content">' + description + '</p>';
    } else {
      return '';
    }
  }

  showModes(display: boolean) {
    this.displayModes = display;
    this.displayModesEvent.emit(display);
  }

  get selectedProductEAN(): string {
    return (
      this.selectedProduct?.fields?.productVariants?.[0]?.fields?.ean ||
      this.selectedProduct?.fields?.productVariants?.[0]?.fields?.sku ||
      ''
    );
  }

  ngOnDestroy(): void {
    // Disconnect all resize observers on component destroy
    this.resizeObservers.forEach((observer) => observer.disconnect());
  }
}
